/* eslint-disable @typescript-eslint/no-empty-function */
import React, { useRef } from "react";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import centroid from "@turf/centroid";
import { polygon } from "@turf/helpers";
import { GeometryType } from "@iventis/domain-model/model/geometryType";
import { useMutation } from "@tanstack/react-query";
import { ModeOfTransport } from "@iventis/domain-model/model/modeOfTransport";
import { ConfirmationDialogComponent } from "@iventis/components";
import { DragAndDropSortable, DropZoneFill, SortableDragAndDropProps, SortableListType, useDragAndDropReordering, useDragAndDropSortable } from "@iventis/drag-and-drop";
import { closestCenter } from "@iventis/drag-and-drop/src/drag-and-drop-helpers";
import { css, Theme } from "@emotion/react";
import { styled, Header4, SolidButton, zIndexes, popoverShadow } from "@iventis/styles";
import { v4 as uuid } from "uuid";

import { convertLengthMeasurement, formatTimeSpanInWordsShort, useConstant, useEventStreamWithStoredValue, ZERO_TIME_SPAN } from "@iventis/utilities";
import { RouteWaypoint } from "@iventis/domain-model/model/routeWaypoint";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Content } from "@iventis/translations";
import { SearchResultGroupInterface } from "@iventis/search/interface/search-result-interface";
import { MapSearchResultType, SearchResultCallBacksType } from "@iventis/search/types/types";
import { UnitOfMeasurement } from "@iventis/domain-model/model/unitOfMeasurement";
import midpoint from "@turf/midpoint";
import { RouteData, RouteInfo, RoutePlannerEvent, routePlannerEventStream } from "./route-planner-event-stream";
import WaypointFinder, { SkeletonWaypoint } from "./waypoint-finder";
import { RouteDistance } from "./route-distance";
import { setModeOfTransportIconName, getMidPointOfLineString } from "./utilities";

export interface RoutePlannerWaypoint extends RouteWaypoint {
    id: string;
}

interface RoutePlannerData extends RouteData {
    waypoints: RoutePlannerWaypoint[];
}

export type RouteOperations = {
    updateRoute: (routeData: Pick<RouteData, "waypoints" | "modeOfTransport">) => void;
    createNewRoute: (mode: ModeOfTransport) => void;
    convertToLine: () => void;
};

const twoEmptyWaypoints = [
    { id: uuid(), name: "", order: 1, coordinates: undefined },
    { id: uuid(), name: "", order: 2, coordinates: undefined },
];

export interface RoutePlannerProps {
    composingNewRoute: boolean;
    operations: RouteOperations;
    createSearch: (mapId: string, callbacks: SearchResultCallBacksType) => SearchResultGroupInterface<MapSearchResultType>[];
    mapId: string;
    unitOfMeasurement: UnitOfMeasurement;
    className?: string;
    unsavedComposition?: boolean;
}

export const RoutePlanner: React.FC<RoutePlannerProps> = ({ composingNewRoute, operations, createSearch, mapId, unitOfMeasurement, className, unsavedComposition }) => {
    const translate = useIventisTranslate();
    const dropZoneContainerRef = useRef<HTMLDivElement>(undefined);
    const [generatingRoute, setGeneratingRoute] = React.useState(false);

    const [showConvertToLineConfirmation, setShowConvertToLineConfirmation] = React.useState(false);

    const initialValue = useEventStreamWithStoredValue(routePlannerEventStream.subject, (event) => {
        switch (event?.type) {
            case RoutePlannerEvent.SetRouteWaypoints: {
                const waypoints = event.payload.waypoints.map((waypoint) => ({ id: uuid(), ...waypoint }));
                setRouteState((routeState) => ({
                    ...routeState,
                    unorderedWaypoints: waypoints.length < 2 ? [...waypoints, { id: uuid(), name: "", order: 2, coordinates: undefined }] : waypoints,
                }));
                if (routeInfo?.distance === 0 || selectedModeOfTransport == null || !loading) {
                    setGeneratingRoute(true);
                }
                if (selectedModeOfTransport != null) {
                    setLoading(false);
                }
                break;
            }
            case RoutePlannerEvent.SetRouteInfo: {
                setRouteState((routeState) => ({ ...routeState, routeInfo: event.payload.routeInfo }));
                // If we have the calculated route information, it must mean we are done generating the route
                setGeneratingRoute(false);
                break;
            }
            case RoutePlannerEvent.SetModeOfTransport: {
                setRouteState((routeState) => ({ ...routeState, selectedModeOfTransport: event.payload.modeOfTransport }));
                if (unorderedWaypoints.some((w) => !(w.name == null || w.name.length === 0))) {
                    setLoading(false);
                }
                break;
            }
            case RoutePlannerEvent.LoadingWaypoints: {
                setLoading(true);
                break;
            }
            default: {
                break;
            }
        }
    });

    const initialRouteData = useConstant<RoutePlannerData>(() => {
        const waypoints = initialValue?.payload.waypoints ?? [];
        return { waypoints: waypoints.map((r) => ({ ...r, id: uuid() })), routeInfo: initialValue?.payload.routeInfo, modeOfTransport: initialValue?.payload.modeOfTransport };
    });

    const [{ unorderedWaypoints, routeInfo, selectedModeOfTransport }, setRouteState] = React.useState<{
        unorderedWaypoints: RoutePlannerWaypoint[];
        routeInfo: RouteInfo;
        selectedModeOfTransport: ModeOfTransport;
    }>({
        unorderedWaypoints: initialRouteData?.waypoints != null && initialRouteData.waypoints.length > 0 ? initialRouteData.waypoints : twoEmptyWaypoints,
        routeInfo: initialRouteData?.routeInfo ?? { distance: 0, duration: ZERO_TIME_SPAN },
        selectedModeOfTransport: composingNewRoute ? ModeOfTransport.Car : initialRouteData?.modeOfTransport,
    });

    const [loading, setLoading] = React.useState(!composingNewRoute && (initialRouteData?.waypoints == null || initialRouteData.waypoints.length < 1));

    const addNewWaypoint = () => {
        setRouteState((routeState) => {
            // Remove any existing empty waypoints
            const definedWaypoints = [
                ...routeState.unorderedWaypoints.filter((waypoint) => waypoint.name != null && waypoint.name.length > 0),
                { id: uuid(), name: "", order: routeState.unorderedWaypoints.length + 1, coordinates: undefined },
            ];
            operations.updateRoute({ waypoints: definedWaypoints, modeOfTransport: routeState.selectedModeOfTransport });
            return { ...routeState, unorderedWaypoints: definedWaypoints };
        });
    };

    const { onDragEvent, sortedItems: waypoints } = useDragAndDropReordering(unorderedWaypoints, (r) => r.order);

    const { mutateAsync: updateRoutesOrder } = useMutation({
        mutationFn: async (newlyOrderedWaypoints: RoutePlannerWaypoint[]) => {
            const waypointsToUpdate = [];
            for (let i = 0; i < newlyOrderedWaypoints.length; i += 1) {
                const waypoint = { ...newlyOrderedWaypoints[i], order: i + 1 };
                if (waypoint?.id != null && waypoints.find((r) => r.id === waypoint.id).order !== i + 1) {
                    waypointsToUpdate.push(waypoint);
                }
            }

            setRouteState((routeState) => {
                const filteredWaypoints = routeState.unorderedWaypoints.filter((waypoint) => !waypointsToUpdate.find(({ id }) => waypoint.id === id));
                const orderedWaypoints = [...filteredWaypoints, ...waypointsToUpdate].sort((a, b) => a.order - b.order);
                operations.updateRoute({ waypoints: orderedWaypoints, modeOfTransport: routeState.selectedModeOfTransport });
                return { ...routeState, unorderedWaypoints: orderedWaypoints };
            });
        },
    });

    const onDragEnd: SortableDragAndDropProps<RoutePlannerWaypoint>["onDragEnd"] = (itemsBeingDragged, over) => {
        if (over) {
            setGeneratingRoute(true);
            onDragEvent(itemsBeingDragged, over);
            const itemOverIndex = waypoints.findIndex((item) => item.id === over.id);
            const itemIndex = waypoints.findIndex((item) => itemsBeingDragged[0].id === item.id);

            // Splice the array to move the new item to the correct position
            const newlyOrderedRoutes = [...waypoints];
            newlyOrderedRoutes.splice(itemOverIndex, 0, newlyOrderedRoutes.splice(itemIndex, 1)[0]);
            updateRoutesOrder(newlyOrderedRoutes);
        }
    };

    const addNewRoute = () => {
        operations.createNewRoute(selectedModeOfTransport);
        setRouteState((routeState) => ({ ...routeState, routeInfo: { distance: 0, duration: ZERO_TIME_SPAN }, unorderedWaypoints: twoEmptyWaypoints }));
    };

    const modesOfTransport = Object.keys(ModeOfTransport).filter((m) => Number.isNaN(Number(m)));

    const setDefinedWaypointsWithNewNameAndCoordinates = (waypointId: string, name: string, coordinates: number[]) => {
        setRouteState((routeState) => {
            const index = routeState.unorderedWaypoints.findIndex((r) => r.id === waypointId);
            let newWaypoints = [...routeState.unorderedWaypoints];
            newWaypoints[index] = { ...newWaypoints[index], name, coordinates };
            const definedWaypoints = newWaypoints.filter((waypoint) => waypoint.name != null && waypoint.name.length > 0);
            newWaypoints = routeState.unorderedWaypoints.length <= 2 ? newWaypoints : definedWaypoints;
            if (definedWaypoints.length > 1) {
                setGeneratingRoute(true);
            }
            operations.updateRoute({ waypoints: newWaypoints, modeOfTransport: routeState.selectedModeOfTransport });
            return { ...routeState, unorderedWaypoints: newWaypoints };
        });
    };

    const handleSearchResultsGroup = (waypointId: string): SearchResultGroupInterface<MapSearchResultType>[] => {
        const callbacksForRoutePlanner: SearchResultCallBacksType = {
            savedMapViewsSearchResultCallback: () => {},
            mapboxSearchResultCallback: (mapboxLocation) => {
                const shortenedName = mapboxLocation.name.split(",").slice(0, 2).join(",");
                setDefinedWaypointsWithNewNameAndCoordinates(
                    waypointId,
                    shortenedName,
                    mapboxLocation.bounds?.length > 2 ? midpoint(mapboxLocation.bounds.slice(0, 2), mapboxLocation.bounds.slice(2, 4)).geometry.coordinates : mapboxLocation.bounds
                );
            },
            mapObjectSearchResultCallback: (mapObject, displayText) => {
                // default GeometryType.Point
                let coords = mapObject.geoJsonFeature.coordinates;
                if (mapObject.geoJsonFeature.type === GeometryType.Polygon) {
                    const mapObjectPolygon = polygon(mapObject.geoJsonFeature.coordinates);
                    coords = centroid(mapObjectPolygon)?.geometry?.coordinates;
                }

                if (mapObject.geoJsonFeature.type === GeometryType.LineString) {
                    const stringMidPoint = getMidPointOfLineString(mapObject.geoJsonFeature);
                    coords = stringMidPoint.geometry.coordinates;
                }
                setDefinedWaypointsWithNewNameAndCoordinates(waypointId, displayText, coords);
            },
            sitemapSearchResultCallback: () => {},
        };
        return createSearch(mapId, callbacksForRoutePlanner);
    };

    const onFocus = (waypointId: string) => {
        setRouteState((routeState) => {
            // If we only have two waypoints, just leave it as is
            if (routeState.unorderedWaypoints.length <= 2) {
                return routeState;
            }
            // Else, remove any empty waypoints except the one in focus
            const definedWaypoints = routeState.unorderedWaypoints.filter((waypoint) => (waypoint.name != null && waypoint.name.length > 0) || waypoint.id === waypointId);
            if (routeState.unorderedWaypoints.length !== definedWaypoints.length) {
                operations.updateRoute({ waypoints: definedWaypoints, modeOfTransport: routeState.selectedModeOfTransport });
            }
            return { ...routeState, unorderedWaypoints: definedWaypoints };
        });
    };

    // Ran when the clear button is clicked on a waypoint
    const onClear = (waypointId: string) => {
        setRouteState((routeState) => {
            const index = routeState.unorderedWaypoints.findIndex((r) => r.id === waypointId);
            const newWaypoints = [...routeState.unorderedWaypoints];
            newWaypoints[index] = { ...newWaypoints[index], name: "", coordinates: undefined };
            let resultingWaypoints = newWaypoints.filter((waypoint) => (waypoint.name != null && waypoint.name.length > 0) || waypoint.id === waypointId);
            // Walways send at least two waypoints
            if (resultingWaypoints.length === 1) {
                resultingWaypoints = newWaypoints;
            }
            operations.updateRoute({ waypoints: resultingWaypoints, modeOfTransport: routeState.selectedModeOfTransport });
            return { ...routeState, unorderedWaypoints: resultingWaypoints };
        });
    };

    return (
        <StyledPlannerContainer data-testid="route-generator-interface" className={className}>
            <StyledHeaderSection>
                <Header4>{translate(Content.map7.routeGenerator.routeGenerator)}</Header4>
                <StyledSolidButton
                    data-testid="new-route-button"
                    color="primary"
                    variant="outlined"
                    onClick={() => addNewRoute()}
                    // New route button is disabled if there are waypoints with no coordinates
                    disabled={unsavedComposition}
                >
                    <FontAwesomeIcon icon={["far", "plus"]} fontSize="14px" /> {translate(Content.map7.routeGenerator.addRoute)}
                </StyledSolidButton>
            </StyledHeaderSection>

            <StyledModeOfTransportContainer>
                {modesOfTransport.map((mode) => (
                    <StyledSolidButton
                        $isSelectedMode={mode === selectedModeOfTransport}
                        $changeColour={mode !== selectedModeOfTransport}
                        id={`${mode}-button`}
                        key={mode}
                        onClick={() => {
                            setRouteState((routeState) => ({ ...routeState, selectedModeOfTransport: ModeOfTransport[mode] }));
                            operations.updateRoute({ modeOfTransport: ModeOfTransport[mode], waypoints });
                            // If we already have a valid route laid out, set generatingRoute to true, because we will be waiting for the geometry to be generated
                            if (waypoints.filter((waypoint) => waypoint.name?.length > 0 || waypoint.coordinates != null)?.length >= 2) {
                                setGeneratingRoute(true);
                            }
                        }}
                        data-testid={`mode-of-transport-${mode}-button`}
                        size="large"
                        type="submit"
                        color="primary"
                        variant={mode === selectedModeOfTransport ? "contained" : "text"}
                    >
                        <FontAwesomeIcon icon={{ prefix: "fas", iconName: setModeOfTransportIconName(ModeOfTransport[mode]) }} /> {mode}
                    </StyledSolidButton>
                ))}
            </StyledModeOfTransportContainer>

            <StyledWaypointsContainer>
                {loading ? (
                    <>
                        <SkeletonWaypoint />
                        <SkeletonWaypoint />
                    </>
                ) : (
                    <StyledDropZoneRef ref={dropZoneContainerRef}>
                        <DragAndDropSortable<RoutePlannerWaypoint>
                            items={unorderedWaypoints}
                            listType={SortableListType.NORMAL}
                            getItems={(id) => [unorderedWaypoints.find((r) => r.id === id)]}
                            calculateNewParent={(id: string) => unorderedWaypoints.find((r) => r.id === id)}
                            findBottomItem={(waypoint) => waypoint?.id}
                            onDragStart={() => null}
                            onDragEnd={onDragEnd}
                            idSuffixes={["bottom"]}
                            collisionDetection={closestCenter}
                            ghostModifiers={[]}
                            modifiers={[restrictToVerticalAxis]}
                        >
                            {unorderedWaypoints.map((waypoint, index) => (
                                <React.Fragment key={`${waypoint.id}-${waypoint.name}`}>
                                    <WaypointFinder
                                        allowDelete={waypoints.length <= 2}
                                        searchResultGroups={handleSearchResultsGroup(waypoint.id)}
                                        waypoint={waypoint}
                                        autoFocus={waypoints.find((w) => w.name == null || w.name.length === 0)}
                                        deleteWaypoint={(id) => {
                                            setGeneratingRoute(true);
                                            setRouteState((routeState) => {
                                                const newWaypoints = routeState.unorderedWaypoints.reduce<RoutePlannerWaypoint[]>(
                                                    (acc, w) => (w.id === id ? acc : [...acc, { ...w, order: acc.length + 1 }]),
                                                    []
                                                );
                                                operations.updateRoute({ waypoints: newWaypoints, modeOfTransport: routeState.selectedModeOfTransport });
                                                setGeneratingRoute(true);
                                                return { ...routeState, unorderedWaypoints: newWaypoints };
                                            });
                                        }}
                                        order={index + 1}
                                        onFocus={() => onFocus(waypoint.id)}
                                        onClear={() => onClear(waypoint.id)}
                                    />
                                    {index === waypoints.length - 1 && (
                                        <StyledDropZone
                                            key={`${waypoint.id}bottom`}
                                            className="drop-zone-fill"
                                            id={`${waypoint.id}bottom`}
                                            containerElementRef={dropZoneContainerRef}
                                        >
                                            <OverIndicator $placement="before" />
                                        </StyledDropZone>
                                    )}
                                </React.Fragment>
                            ))}
                        </DragAndDropSortable>
                    </StyledDropZoneRef>
                )}
            </StyledWaypointsContainer>
            {unorderedWaypoints.every((waypoint) => waypoint.name?.length) && (
                <StyledSolidButton
                    disabled={unorderedWaypoints.length >= 25}
                    data-testid="add-waypoint-button"
                    color="primary"
                    variant="outlined"
                    onClick={() => {
                        dropZoneContainerRef.current?.scrollTo({ top: dropZoneContainerRef.current.scrollHeight, behavior: "smooth" });
                        addNewWaypoint();
                    }}
                >
                    <FontAwesomeIcon icon={["far", "plus"]} /> {translate(Content.map7.routeGenerator.addStop)}
                </StyledSolidButton>
            )}
            {unorderedWaypoints.filter((waypoint) => waypoint.name?.length).length >= 2 && (
                <StyledSolidButton data-testid="convert-line-button" color="primary" variant="outlined" onClick={() => setShowConvertToLineConfirmation(true)}>
                    {translate(Content.map11.route_convert_line)}
                </StyledSolidButton>
            )}
            {/* if have route conditionally show the RouteDistance component */}
            {routeInfo?.distance > 0 && (
                <RouteDistance
                    translate={translate}
                    modeOfTransport={selectedModeOfTransport}
                    travelDistance={convertLengthMeasurement(routeInfo.distance, unitOfMeasurement)}
                    travelTime={formatTimeSpanInWordsShort(routeInfo.duration)}
                    isGeneratingRoute={generatingRoute}
                />
            )}
            <ConfirmationDialogComponent
                show={showConvertToLineConfirmation}
                handleConfirm={() => {
                    operations.convertToLine();
                }}
                handleCancel={() => {
                    setShowConvertToLineConfirmation(false);
                }}
                title={translate(Content.map11.route_convert_confirmation.title)}
                confirmText={translate(Content.common.buttons.confirm)}
                cancelText={translate(Content.common.buttons.cancel)}
            >
                {translate(Content.map11.route_convert_confirmation.message)}
            </ConfirmationDialogComponent>
        </StyledPlannerContainer>
    );
};

const StyledPlannerContainer = styled.div`
    box-sizing: border-box;
    padding: 15px;
    background: ${(props: { theme: Theme }) => props.theme.secondaryColors.blank};
    border-radius: 4px;
    box-shadow: ${popoverShadow};
    display: flex;
    flex-direction: column;
    gap: 10px;
    z-index: ${zIndexes.routePlanner};
`;

const StyledDropZoneRef = styled.div`
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const StyledDropZone = styled(DropZoneFill)`
    top: 0;
    width: 100%;
    height: 100%;
    position: absolute;
    pointer-events: none;
`;

const OverIndicator = styled.div<{ $placement: ReturnType<typeof useDragAndDropSortable>["placement"] }>`
    width: 2px;
    background-color: ${(props: { theme: Theme }) => props.theme.tertiaryColors.primary};
    height: 100%;
    position: absolute;
    ${(props) =>
        props.$placement === "before"
            ? css`
                  left: -3.5px;
              `
            : props.$placement === "after"
            ? css`
                  right: -3.5px;
              `
            : ""}
`;

const StyledWaypointsContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex-grow: 1;
    max-height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
`;

const StyledSolidButton = styled(SolidButton)<{ $isSelectedMode?: boolean; $changeColour?: boolean }>`
    display: flex;
    gap: 15px;
    justify-content: center;
    ${({ $changeColour, theme }) =>
        $changeColour &&
        css`
            color: ${theme.primaryColors.subdued70};
        `}
    ${({ $isSelectedMode, theme }) =>
        $isSelectedMode &&
        css`
            background-color: ${theme.primaryColors.subdued70};
            :hover {
                background-color: ${theme.primaryColors.subdued70};
            }
        `};
`;

const StyledModeOfTransportContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 15px;
`;

const StyledHeaderSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 10px;
    border-bottom: 1px solid ${({ theme }) => theme.shades.darkBorder};
`;
